import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"

import { CircularProgress, Grid } from "@mui/material"
import { Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { DebouncedInput } from "../../../../components/form/DebouncedInput"
import { LabelsTable } from "../../../../components/tables/LabelsTable"
import { KonanAPI } from "../../../../services/KonanAPI"
import type { RootState } from "../../../../store/ReduxStore"
import type { PaginatedLabelList } from "../../../../types/generated/api/PaginatedLabelList"

/**
 * Labels component that displays a searchable list of project labels.
 * Supports pagination and search functionality for label management.
 */
export function Labels(): React.ReactElement {
  // Redux hooks
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  // Translation hook
  const { t } = useTranslation("tables", {
    keyPrefix: "labels",
  })

  // Constants
  const pageSize = 10

  // State hooks
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [searchKey, setSearchKey] = useState<string | null>(null)

  // Data fetching queries
  // Fetch labels list
  const { data: labelList, isLoading: isLabelListLoading } = useQuery<AxiosResponse<PaginatedLabelList>, AxiosError>(
    ["label-list", currentProject?.uuid, currentPage, pageSize, searchKey],
    () =>
      KonanAPI.RetrieveLabels({
        project_uuid: currentProject?.uuid as string,
        page: currentPage + 1,
        pageSize: pageSize,
        search: searchKey,
      }),
    {
      keepPreviousData: true,
    },
  )

  return (
    <Grid container>
      {isLabelListLoading ? (
        <Grid container style={{ minHeight: "250px" }}>
          <Grid item xs={12} display={"flex"} justifyContent="center" alignItems={"flex-end"}>
            <CircularProgress />
          </Grid>

          <Grid item xs={12} display={"flex"} justifyContent="center">
            <Typography variant={"a"}>{t("loading")}</Typography>
          </Grid>
        </Grid>
      ) : (
        <Fragment>
          <Grid item my={1}>
            <DebouncedInput
              callback={(value: string | null) => {
                setSearchKey(value)
              }}
              variant="filled"
            />
          </Grid>

          <LabelsTable
            labels={labelList?.data}
            page={currentPage}
            setPage={(page) => {
              setCurrentPage(page)
            }}
          />
        </Fragment>
      )}
    </Grid>
  )
}
